import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { TreadAgentMessageFile } from '~hooks/useTreadAgent/models';

// Caps the preview image; the chip below is bounded to the same width so they line up.
const PREVIEW_MAX_WIDTH_IN_PX = 200;
const PREVIEW_MAX_HEIGHT_IN_PX = 160;

interface MessageAttachmentsProps {
  files: TreadAgentMessageFile[];
}

// Renders a message's uploaded files in the transcript. Every file shows a chip (file icon
// + filename); files that have a preview (images and PDFs) additionally show the preview
// image stacked above the chip. Each opens the stored file in a new tab.
export default function MessageAttachments({ files }: MessageAttachmentsProps) {
  const theme = useTheme();

  if (files.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.75,
        justifyContent: 'flex-end',
      }}
    >
      {files.map((file) => (
        <Link
          href={file.url}
          key={file.id}
          rel="noopener"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            maxWidth: `${PREVIEW_MAX_WIDTH_IN_PX}px`,
            textDecoration: 'none',
          }}
          target="_blank"
        >
          {file.previewUrl && (
            <Box
              alt={file.filename}
              component="img"
              src={file.previewUrl}
              sx={{
                borderRadius: theme.brandV2.borderRadius,
                display: 'block',
                maxHeight: `${PREVIEW_MAX_HEIGHT_IN_PX}px`,
                maxWidth: '100%',
                objectFit: 'cover',
              }}
            />
          )}

          <Box
            sx={{
              alignItems: 'center',
              bgcolor: theme.brandV2.colors.treadGray8,
              borderRadius: theme.brandV2.borderRadius,
              display: 'flex',
              gap: 0.5,
              px: 1,
              py: 0.75,
            }}
          >
            <InsertDriveFileOutlined
              sx={{ color: theme.brandV2.colors.treadGray2, fontSize: 16 }}
            />
            <Typography
              noWrap
              sx={{ color: theme.brandV2.colors.treadGray1, fontSize: '12px' }}
              title={file.filename}
            >
              {file.filename}
            </Typography>
          </Box>
        </Link>
      ))}
    </Box>
  );
}
